// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — controllers for the balance/mechanism sims (real game logic, not shipped into the
// live hot path). Each returns the next place(i, x, y) to apply.
//
//  · SmartBot places every bead at its known solution position → the web has zero crossings,
//    so it proves every web is solvable and gives the reference move count (≈ n).
//  · BlindBot scatters beads at random and never places them where they belong → the control
//    that shows reading the tangle is load-bearing (a flailer almost never solves).
//  · SloppyBot flails for a while, THEN solves — it solves fine in Loom (no limit) but blows
//    Snarl's move budget before it finishes. That sign flip is what proves the budget rule
//    (Snarl's sudden death) is load-bearing, not decoration.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import type { GameState } from './game';

export interface Place {
  i: number;
  x: number;
  y: number;
}
export interface Controller {
  next(s: GameState): Place | null;
}

function range(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i);
}

/** Places each bead at its solution position — completes any web in ≤ n moves. */
export class SmartBot implements Controller {
  private idx = -1;
  private queue: number[] = [];
  next(s: GameState): Place | null {
    if (s.index !== this.idx) {
      this.idx = s.index;
      this.queue = range(s.graph.n);
    }
    const k = this.queue.shift();
    if (k === undefined) return null;
    const sol = s.graph.solution[k];
    return { i: k, x: sol.x, y: sol.y };
  }
}

/** Scatters beads at random forever — deterministic from the seed, essentially never solves. */
export class BlindBot implements Controller {
  private rng: Rng;
  constructor(seed: number) {
    this.rng = makeRng((seed ^ 0x1a2b3c4d) >>> 0);
  }
  next(s: GameState): Place | null {
    const k = Math.floor(this.rng() * s.graph.n) % s.graph.n;
    return { i: k, x: 0.12 + this.rng() * 0.76, y: 0.12 + this.rng() * 0.76 };
  }
}

/**
 * Flails `n` random placements, then places every bead correctly. It always solves given
 * enough moves (Loom), but the wasted first half blows a tight move budget (Snarl) — the
 * same policy, harmless in one mode and fatal in the other.
 */
export class SloppyBot implements Controller {
  private rng: Rng;
  private idx = -1;
  private step = 0;
  private queue: number[] = [];
  constructor(seed: number) {
    this.rng = makeRng((seed ^ 0x5bce7f21) >>> 0);
  }
  next(s: GameState): Place | null {
    const n = s.graph.n;
    if (s.index !== this.idx) {
      this.idx = s.index;
      this.step = 0;
      this.queue = range(n);
    }
    if (this.step < n) {
      this.step++;
      const k = Math.floor(this.rng() * n) % n;
      return { i: k, x: 0.12 + this.rng() * 0.76, y: 0.12 + this.rng() * 0.76 };
    }
    const k = this.queue.shift();
    if (k === undefined) return null;
    const sol = s.graph.solution[k];
    return { i: k, x: sol.x, y: sol.y };
  }
}
